// src/context/AuthContext.js
import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";

const AuthContext = createContext();

const AUTH_TOKEN_CHANGED = "auth-token-changed";

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(null);

  const syncAuthState = useCallback(() => {
    const token = localStorage.getItem("token");
    const changed = tokenRef.current !== null && tokenRef.current !== token;
    tokenRef.current = token;
    setIsAuthenticated(!!token);
    return changed;
  }, []);

  useEffect(() => {
    syncAuthState();
    setLoading(false);

    const notifyTokenChange = () => {
      window.dispatchEvent(new CustomEvent(AUTH_TOKEN_CHANGED));
    };

    const handleStorageChange = (event) => {
      if (event.key === "token" || event.key === null) {
        syncAuthState();
        notifyTokenChange();
      }
    };

    const handleTokenCheck = () => {
      const changed = syncAuthState();
      if (changed) {
        notifyTokenChange();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", handleTokenCheck);
    document.addEventListener("visibilitychange", handleTokenCheck);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleTokenCheck);
      document.removeEventListener("visibilitychange", handleTokenCheck);
    };
  }, [syncAuthState]);

  const login = (token) => {
    localStorage.setItem("token", token);
    tokenRef.current = token;
    setIsAuthenticated(true);
    window.dispatchEvent(new CustomEvent(AUTH_TOKEN_CHANGED));
  };

  const logout = () => {
    localStorage.removeItem("token");
    tokenRef.current = null;
    setIsAuthenticated(false);
    window.dispatchEvent(new CustomEvent(AUTH_TOKEN_CHANGED));
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export const AUTH_TOKEN_CHANGED_EVENT = AUTH_TOKEN_CHANGED;
